import { useMemo } from "react";
import { useAuthContext } from "../context/AuthContext/AuthContext";
import {
  hasPriceCategoryAccess,
  shouldShowPriceForCategories,
} from "../helpers/getCategoryDescendants";
import * as shopQueries from "../../queries/shop";
import { usePersistedQuery } from "./usePersistedQuery";
import { QUERY_TTL } from "../helpers/persistedQueryCache";

export const usePriceCategoryFilter = (
  productCategoryIds: number[]
): boolean => {
  const authCtx = useAuthContext();

  const showPriceCategories = authCtx.user?.showPriceCategories;
  const allowedCategories = Array.isArray(showPriceCategories)
    ? showPriceCategories
    : [];
  const hasPriceAccess =
    authCtx.isAuthenticated &&
    !!authCtx.user &&
    authCtx.user.showPrice !== false &&
    hasPriceCategoryAccess(showPriceCategories);
  const needsCategoryTree = hasPriceAccess && allowedCategories.length > 0;

  const { data: categoryData } = usePersistedQuery<
    shopQueries.GetAllProductCategoriesResponse
  >(shopQueries.getAllProductCategories, {
    skip: !needsCategoryTree,
    persistKey: "allProductCategories",
    persistTtl: QUERY_TTL.allCategories,
  });

  const shouldShowPrice = useMemo(() => {
    if (!hasPriceAccess || !needsCategoryTree) {
      return false;
    }

    if (!categoryData?.productCategories?.nodes) {
      return false;
    }

    return shouldShowPriceForCategories(
      productCategoryIds,
      allowedCategories,
      categoryData.productCategories.nodes
    );
  }, [
    hasPriceAccess,
    needsCategoryTree,
    allowedCategories,
    categoryData?.productCategories?.nodes,
    productCategoryIds,
  ]);

  return shouldShowPrice;
};
